/**
 * Canonical types for the contracts feature.
 * Consuming sites map their API responses to these types.
 */

import type { ContractRetainerSummary, ContractRecurringServicesSummary } from "./contractList.types";

export const Site = {
  CustomerSuccess: "customer-success",
  CustomerExperience: "customer-experience",
} as const;

export type SiteContext = (typeof Site)[keyof typeof Site];

export type DateValue = Date | string | { year: number; month: number; day: number } | null;

export interface Account {
  id?: number;
  accountName?: string | undefined;
  accountNumber?: string | undefined;
  accountType?: number;
  active?: boolean;
  additionalAddressInformation?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  alternatePhone1?: string | undefined;
  alternatePhone2?: string | undefined;
  assetValue?: number;
  billToAccountPhysicalLocationID?: number;
  billToAdditionalAddressInformation?: string | undefined;
  billToAddress1?: string | undefined;
  billToAddress2?: string | undefined;
  billToAddressToUse?: number;
  billToAttention?: string | undefined;
  billToCity?: string | undefined;
  billToCountryID?: number;
  billToState?: string | undefined;
  billToZipCode?: string | undefined;
  city?: string | undefined;
  clientPortalActive?: boolean;
  competitorID?: number;
  country?: string | undefined;
  countryID?: number;
  createDate?: Date;
  currencyID?: number;
  invoiceEmailMessageID?: number;
  invoiceMethod?: number;
  invoiceNonContractItemsToParentAccount?: boolean;
  invoiceTemplateID?: number;
  keyAccountIcon?: number;
  marketSegmentID?: number;
  phone?: string | undefined;
  postalCode?: string | undefined;
  quoteEmailMessageID?: number;
  quoteTemplateID?: number;
  sicCode?: string | undefined;
  state?: string | undefined;
  stockMarket?: string | undefined;
  stockSymbol?: string | undefined;
  taskFireActive?: boolean;
  taxExempt?: boolean;
  taxID?: string | undefined;
  taxRegionID?: number;
  territoryID?: number;
  webAddress?: string | undefined;
  readonly userDefinedFieldsObject?: string | undefined;
  axNumber?: string | undefined;
  accountExecutiveEmail?: string | undefined;
  lastActivityDate?: Date;
  parentAccountID?: number | undefined;
  userDefinedFieldsJson?: string | undefined;
  subdomainPrefix?: string | undefined;
  subdomainSettingsJson?: string | undefined;
  axReferences?: AXReference[] | undefined;
  teams?: Team[] | undefined;
  noManualAuthRequired?: boolean;
  onboardingConfigurationComplete?: boolean;
  costCenters?: CostCenter[] | undefined;
}

export interface CostCenter {
  id?: number;
  costCenterId?: string | undefined;
  accountId?: number | undefined;
  account?: Account;
  name?: string | undefined;
  description?: string | undefined;
  costCenterPurchaseOrders?: CostCenterPurchaseOrders[] | undefined;
  createTime?: Date;
  modifyTime?: Date;
  isUnallocated?: boolean;
  configItems?: CostCenterConfigItem[] | undefined;
}

export interface PurchaseOrder {
  id?: number;
  poNumber?: string | undefined;
  costCenterPurchaseOrders?: CostCenterPurchaseOrders[] | undefined;
}

export interface CostCenterPurchaseOrders {
  id?: number;
  costCenterId?: number;
  purchaseOrderId?: number | undefined;
  cloudType: string | undefined;
  costCenter: CostCenter;
  purchaseOrder: PurchaseOrder;
}

export interface CostCenterConfigItem {
  configurationItemId?: number;
  configurationItem?: ConfigurationItem;
  costCenterId?: number;
  costCenter?: CostCenter;
  createTime?: Date;
  modifyTime?: Date;
  licenseCount?: number | undefined;
}

export interface ConfigurationItem {
  id?: number;
  accountID?: number | undefined;
  accountPhysicalLocationID?: number | undefined;
  productID?: number | undefined;
  contractID?: number | undefined;
  serviceID?: number | undefined;
  serviceBundleID?: number | undefined;
  contactID?: number | undefined;
  vendorID?: number | undefined;
  installedByID?: number | undefined;
  installedByContactID?: number | undefined;
  parentInstalledProductID?: number | undefined;
  contractServiceID?: number | undefined;
  contractServiceBundleID?: number | undefined;
  serviceLevelAgreementID?: number | undefined;
  active?: boolean;
  createDate?: Date;
  installDate?: Date;
  notes?: string | undefined;
  referenceNumber?: string | undefined;
  referenceTitle?: string | undefined;
  serialNumber?: string | undefined;
  warrantyExpirationDate?: Date;
  type?: number;
  location?: string | undefined;
  lastModifiedTime?: Date;
  rmmDeviceID?: number | undefined;
  rmmDeviceUID?: string | undefined;
  rmmDeviceAuditArchitectureID?: number | undefined;
  rmmDeviceAuditDescription?: string | undefined;
  rmmDeviceAuditDisplayAdaptorID?: number | undefined;
  rmmDeviceAuditDomainID?: number | undefined;
  rmmDeviceAuditExternalIPAddress?: string | undefined;
  rmmDeviceAuditHostname?: string | undefined;
  rmmDeviceAuditIPAddress?: string | undefined;
  rmmDeviceAuditMacAddress?: string | undefined;
  rmmDeviceAuditManufacturerID?: number | undefined;
  rmmDeviceAuditMemoryBytes?: number | undefined;
  rmmDeviceAuditModelID?: number | undefined;
  rmmDeviceAuditMotherboardID?: number | undefined;
  rmmDeviceAuditOperatingSystem?: string | undefined;
  rmmDeviceAuditProcessorID?: number | undefined;
  rmmDeviceAuditServicePackID?: number | undefined;
  rmmDeviceAuditStorageBytes?: number | undefined;
  rmmDeviceAuditDeviceTypeID?: number | undefined;
  rmmDeviceAuditSNMPLocation?: string | undefined;
  rmmDeviceAuditSNMPName?: string | undefined;
  rmmDeviceAuditSNMPContact?: string | undefined;
  rmmDeviceAuditMobileNetworkOperatorID?: number | undefined;
  rmmDeviceAuditMobileNumber?: string | undefined;
  rmmDeviceAuditMissingPatchCount?: number | undefined;
  rmmOpenAlertCount?: number | undefined;
  rmmDeviceAuditLastUser?: string | undefined;
  dattoSerialNumber?: string | undefined;
  dattoInternalIP?: string | undefined;
  dattoRemoteIP?: string | undefined;
  dattoHostname?: string | undefined;
  dattoProtectedKilobytes?: number | undefined;
  dattoUsedKilobytes?: number | undefined;
  dattoAvailableKilobytes?: number | undefined;
  dattoPercentageUsed?: number | undefined;
  dattoOffsiteUsedBytes?: number | undefined;
  dattoOSVersionID?: number | undefined;
  dattoZFSVersionID?: number | undefined;
  dattoKernelVersionID?: number | undefined;
  dattoNICSpeedKilobitsPerSecond?: number | undefined;
  dattoDeviceMemoryMegabytes?: number | undefined;
  dattoUptimeSeconds?: number | undefined;
  dattoNumberOfAgents?: number | undefined;
  dattoNumberOfDrives?: number | undefined;
  dattoDrivesErrors?: boolean;
  dattoNumberOfVolumes?: number | undefined;
  numberOfUsers?: number;
  dattoLastCheckInDateTime?: Date | undefined;
  userDefinedFieldsJson?: string | undefined;
  installedProductCategoryID?: number | undefined;
  teamConfigItems?: TeamConfigItem[] | undefined;
  costCenter?: CostCenterConfigItem;
}

export interface AXReference {
  id?: number;
  axNumber?: string | undefined;
  axTitle?: string | undefined;
  account?: Account;
  dateCreated?: Date;
  createdByEntraId?: string | undefined;
  dateModified?: Date | undefined;
  modifiedByEntraId?: string | undefined;
}

export interface Team {
  id?: number;
  teamName?: string | undefined;
  account?: Account;
  teamTicketCategoryMappings?: TeamTicketCategoryMapping[] | undefined;
  contactTeamRole?: ContactTeamRole[] | undefined;
  teamContracts?: TeamContract[] | undefined;
  teamConfigItems?: TeamConfigItem[] | undefined;
  deleted?: boolean;
  isDefaultGroup?: boolean;
  permissionStatements?: string | undefined;
}

export interface ContactTeamRole {
  id?: number;
  contactId?: number;
  contact: Contact;
  teamId?: number;
  team: Team;
  roleId?: number | undefined;
  role?: Role;
}

export interface Role {
  id?: number;
  roleName?: string | undefined;
  contactTeamRole?: ContactTeamRole[] | undefined;
}

export interface TeamConfigItem {
  configurationItemId?: number;
  configurationItem?: ConfigurationItem;
  teamId?: number;
  team?: Team;
}

export interface TeamTicketCategoryMapping {
  teamId?: number;
  team?: Team;
  ticketCategoryMappingId?: number;
  ticketCategoryMapping?: TicketCategoryMapping;
  contractId?: number;
  enabledIssueType?: string | undefined;
}

export interface TicketCategoryMapping {
  id?: number;
  contractSLA?: ContractSLA;
  contractSLAId?: number;
  ticketCategoryId?: number;
  listOfValidIssueTypes?: number[] | undefined;
  teamTicketCategoryMappings?: TeamTicketCategoryMapping[] | undefined;
}

export interface ContractSLA {
  id?: number;
  contractCategory?: number;
  serviceLevelAgreementID?: number;
  contractType?: string | undefined;
  ticketCategoryMappings?: TicketCategoryMapping[] | undefined;
}

export interface TeamContract {
  contractId?: number;
  contract?: Contract;
  teamId?: number;
  team?: Team;
}

export interface Contract {
  id?: number;
  userDefinedFieldsJson?: string | undefined;
  readonly userDefinedFieldsObject?: string | undefined;
  partnerName?: string | undefined;
  accountID?: number | undefined;
  axNumber?: string | undefined;
  billingPreference?: number;
  businessDivisionSubdivisionID?: number;
  compliance?: boolean;
  contactID?: number | undefined;
  contactName?: string | undefined;
  contractCategory?: number | undefined;
  contractName?: string | undefined;
  contractNumber?: string | undefined;
  contractPeriodType?: string | undefined;
  contractType?: number;
  description?: string | undefined;
  endDate?: Date;
  estimatedCost?: number | undefined;
  estimatedHours?: number | undefined;
  estimatedRevenue?: number | undefined;
  exclusionContractID?: number | undefined;
  internalCurrencyOverageBillingRate?: number | undefined;
  internalCurrencySetupFee?: number | undefined;
  isDefaultContract?: boolean;
  opportunityID?: number | undefined;
  overageBillingRate?: number | undefined;
  purchaseOrderNumber?: string | undefined;
  renewedContractID?: number | undefined;
  serviceLevelAgreementID?: number | undefined;
  setupFee?: number | undefined;
  setupFeeAllocationCodeID?: number | undefined;
  startDate?: Date;
  status?: number;
  timeReportingRequiresStartAndStopTimes?: number;
  deleted?: boolean;
  systemGuid?: string | undefined;
  parentSystemGuid?: string | undefined;
  teamContracts?: TeamContract[] | undefined;
}

export interface AccountContractRequest {
  pageNumber?: number;
  pageSize?: number;
  searchString?: string | undefined;
  accountId?: number | undefined;
  contractTypes?: number[] | undefined;
  accounts?: number[] | undefined;
  status?: number | undefined;
  orderBy?: string | undefined;
  orderDirection?: string | undefined;
}

export interface AccountResponse {
  responseStatus?: number;
  errorMessage?: string | undefined;
  accounts?: Account[] | undefined;
}

export interface AccountContractResponse {
  isLoading?: boolean;
  error?: unknown;
  gridData?: ExtendedContract[] | undefined;
  resultInfo?: PagedResultData | undefined;
}

export interface RetainerMonthlyExpenseDto {
  label?: string | undefined;
  amounts?: RetainerExpenseAmountDto[] | undefined;
}

export interface RetainerExpenseAmountDto {
  expenseType?: RetainerExpenseType;
  amount?: number;
}

export interface Contact {
  id?: number;
  active?: number;
  alternatePhone?: string | undefined;
  bulkEmailOptOut?: boolean;
  bulkEmailOptOutTime?: Date;
  accountPhysicalLocationId?: number;
  addressLine?: string | undefined;
  addressLine1?: string | undefined;
  additionalAddressInformation?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  country?: string | undefined;
  zipCode?: string | undefined;
  roomNumber?: string | undefined;
  countryId?: number;
  createDate?: Date;
  eMailAddress?: string | undefined;
  eMailAddress2?: string | undefined;
  eMailAddress3?: string | undefined;
  externalId?: string | undefined;
  faxNumber?: string | undefined;
  mobilePhone?: string | undefined;
  phone?: string | undefined;
  extension?: string | undefined;
  title?: string | undefined;
  firstName?: string | undefined;
  middleInitial?: string | undefined;
  lastName?: string | undefined;
  namePrefix?: number;
  nameSuffix?: number;
  note?: string | undefined;
  notification?: boolean;
  primaryContact?: boolean;
  solicitationOptOut?: boolean;
  solicitationOptOutTime?: Date;
  surveyOptOut?: boolean;
  facebookUrl?: string | undefined;
  twitterUrl?: string | undefined;
  linkedInUrl?: string | undefined;
  accountID?: number | undefined;
  contactTeamRoles?: ContactTeamRole[] | undefined;
  ticketAdditionalContacts?: TicketAdditionalContact[] | undefined;
  fullProjectAccess?: boolean;
  lastActivityDate?: Date;
  lastModifiedDate?: Date;
  userDefinedFieldsJson?: string | undefined;
}

export interface TicketAdditionalContact {
  id?: number;
  contactId?: number;
  ticketId?: number;
}

export interface PagedResultData {
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalCount?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

export interface ContractReport {
  accountId?: number;
  expireIn30?: number;
  expireIn60?: number;
  expireIn90?: number;
  expireOver90?: number;
  contractTypeStats?: ContractTypeStats[] | undefined;
}

export interface ContractTypeStats {
  counted?: number;
  contractCategory?: number;
  contractType?: number;
}

export interface ContractDto {
  id?: number;
  userDefinedFieldsJson?: string | undefined;
  accountID?: number | undefined;
  businessDivisionSubdivisionID?: number;
  compliance?: boolean;
  contactID?: number | undefined;
  contactName?: string | undefined;
  contractCategory?: number | undefined;
  contractName?: string | undefined;
  contractNumber?: string | undefined;
  contractPeriodType?: string | undefined;
  contractType?: number;
  contractServiceType?: string | undefined;
  contractVendor?: string | undefined;
  contractSubType?: string | undefined;
  description?: string | undefined;
  endDate?: Date;
  purchaseOrderNumber?: string | undefined;
  renewedContractID?: number | undefined;
  serviceLevelAgreementID?: number | undefined;
  startDate?: Date;
  status?: number;
  timeReportingRequiresStartAndStopTimes?: number;
  contractBlock?: ContractBlockDto;
  contractTicketPurchase?: ContractTicketPurchaseDto;
}

export interface ContractTicketPurchaseDto {
  id?: number;
  contractID?: number | undefined;
  datePurchased?: Date;
  startDate?: Date;
  endDate?: Date;
  ticketsPurchased?: number;
  ticketsUsed?: number;
  status?: number;
}

export interface Invoice {
  invoiceId?: string | undefined;
  invoiceDate?: string | undefined;
  customerPo?: string | undefined;
  customerRequisition?: string | undefined;
  invoiceAmount?: number;
  tagFilter?: string | undefined;
  tag?: string | undefined;
  tenant?: string | undefined;
  serviceProjectName?: string | undefined;
}

export interface DetailedContractRetainerExpenseDto {
  retainerContractSpendResults?: RetainerMonthlyExpenseDto[] | undefined;
  retainerMonthlyBreakdown?: RetainerMonthlyBreakdownDto[] | undefined;
}

export interface RetainerMonthlyBreakdownDto {
  year?: number;
  month?: number;
  classification?: RetainerExpenseType;
  deposits?: number;
  usage?: number;
  status?: RetainerBreakdownStatus;
  accountId?: number;
  contractId?: number;
  invoiceIds?: number[] | undefined;
  ticketNumbers?: string[] | undefined;
  projectIds?: number[] | undefined;
}

export enum RetainerBreakdownStatus {
  Consumed = "Consumed",
  Pending = "Pending",
  Invoiced = "Invoiced",
  Other = "Other",
}

export enum RetainerExpenseType {
  RetainerSigning = "RetainerSigning",
  TicketExpense = "TicketExpense",
  ProjectTaskExpense = "ProjectTaskExpense",
  ContractExpense = "ContractExpense",
  Other = "Other",
}

export enum RecurringServicesRoleType {
  ExpertSupport = "ExpertSupport",
  ProactiveSupport = "ProactiveSupport",
  DedicatedEngineer = "DedicatedEngineer",
}

export interface RoleUsageDto {
  roleType?: RecurringServicesRoleType;
  hours?: number;
  dollars?: number;
  percentageOfTotal?: number;
}

export interface RecurringServicesContractBurndownDto {
  contractId?: number;
  contractName?: string | undefined;
  accountId?: number;
  isActive?: boolean;
  contractStartDate?: Date;
  contractEndDate?: Date | undefined;
  totalPurchased?: number;
  totalPurchasedBilled?: number;
  totalPurchasedUnbilled?: number;
  totalUsed?: number;
  currentBalance?: number;
  totalDollarsUsageBilled?: number;
  totalDollarsUsageUnbilled?: number;
  utilizationPercentage?: number;
  estimatedMonthlyBurnRate?: number;
  estimatedDaysRemaining?: number | undefined;
  totalChargeCount?: number;
  totalTimeEntryCount?: number;
  proactiveDollarsUsed?: number;
  nonProactiveDollarsUsed?: number;
  usageByRole?: RoleUsageDto[] | undefined;
  expertSupportTier?: string;
}

export enum ExpertSupportDetailType {
  Charge = "Charge",
  TimeEntry = "TimeEntry",
}

export enum ExpertSupportDetailStatus {
  Pending = "Pending",
  Invoiced = "Invoiced",
  Consumed = "Consumed",
}

export interface RecurringServicesContractDetailRecordDto {
  accountId?: number;
  contractId?: number;
  dataLoadedDateTime?: Date;
  detailType?: ExpertSupportDetailType;
  hoursWorked?: number;
  month?: number;
  purchasedAmount?: number;
  recordCount?: number;
  status?: ExpertSupportDetailStatus;
  ticketNumbers?: string[];
  usageAmount?: number;
  year?: number;
}

export interface RecurringServicesContractDetailDto {
  burndown?: RecurringServicesContractBurndownDto;
  detailRecords?: RecurringServicesContractDetailRecordDto[] | undefined;
  monthlyBreakdowns?: RecurringServicesContractMonthlyBreakdownDto[] | undefined;
}

export interface RecurringServicesContractMonthlyBreakdownDto {
  contractId?: number;
  accountId?: number;
  year?: number;
  month?: number;
  openingBalance?: number;
  closingBalance?: number;
  chargesAdded?: number;
  consumed?: number;
  usageByRole?: RoleUsageDto[];
}

export interface RecurringServicesBreakdownRowDto {
  year?: number;
  month?: number;
  roleType?: RecurringServicesRoleType;
  hours?: number;
  dollars?: number;
  percentageOfTotal?: number;
  openingBalance?: number;
  closingBalance?: number;
  chargesAdded?: number;
}

export interface ContractBlockDto {
  id?: number;
  contractID?: number | undefined;
  datePurchased?: Date;
  startDate?: Date;
  endDate?: Date;
  hours?: number;
  hoursApproved?: number;
  status?: number;
}

export type ExtendedContract = ContractDto & {
  projectCount: number;
  contractTypeName: string | undefined;
  billingTypeLabel: string | undefined;
  accountName: string | undefined;
  projectDuration: number | undefined;
  projectDurationLabel: string | undefined;
  retainers: ContractRetainerSpend[] | undefined;
  retainerSummary?: ContractRetainerSummary | null;
  recurringServicesSummary?: ContractRecurringServicesSummary | null;
}

export interface ContractRetainerSpend {
  id?: number;
  accountId?: number;
  contractId?: number;
  contractActive?: boolean;
  hasAnyRetainerActive?: boolean;
  totalActiveRetainerAmount?: number;
  billedSpend?: number;
  pendingSpend?: number;
  billedOverage?: number;
  pendingOverage?: number;
  dataLoadedDateTime?: Date;
}
